// Pure filter for the network selector in the page header.
// Single source of truth for what "show only mainnet / testnet" means.

export type ComponentStatus =
  | 'operational'
  | 'degraded_performance'
  | 'partial_outage'
  | 'major_outage'
  | 'maintenance'
  | 'no_data';

export type NetworkFilter = 'all' | 'mainnet' | 'testnet';

export interface ComponentSummary {
  id: number;
  key: string;
  displayName: string;
  group: string;
  network: 'mainnet' | 'testnet';
  status: ComponentStatus;
  uptimePct90d: number;
  lastBuildAt: string | null;
  lastBuildStatus: string | null;
}

export interface ComponentGroup {
  group: string;
  uptimePct90d: number;
  status: ComponentStatus;
  components: ComponentSummary[];
}

const STATUS_RANK: Record<ComponentStatus, number> = {
  operational: 0,
  degraded_performance: 1,
  maintenance: 2,
  partial_outage: 3,
  major_outage: 4,
  no_data: -1,
};

function worstOfStatuses(
  statuses: readonly ComponentStatus[],
): ComponentStatus {
  let worst: ComponentStatus = 'no_data';
  let worstRank = STATUS_RANK.no_data;
  for (const status of statuses) {
    const rank = STATUS_RANK[status];
    if (rank > worstRank) {
      worst = status;
      worstRank = rank;
    }
  }
  return worst;
}

function averageUptime(values: readonly number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((acc, v) => acc + v, 0) / values.length;
}

export function filterGroupsByNetwork(
  groups: readonly ComponentGroup[],
  filter: NetworkFilter,
): ComponentGroup[] {
  if (filter === 'all') {
    return groups.slice();
  }
  const result: ComponentGroup[] = [];
  for (const group of groups) {
    const components = group.components.filter((c) => c.network === filter);
    if (components.length === 0) continue;
    // Average over components with real uptime data: a fully-down
    // component (uptimePct90d === 0) is a legitimate value and must count,
    // but no_data components carry no measurement and are excluded.
    const measured = components.filter((c) => c.status !== 'no_data');
    const uptimePct90d = averageUptime(measured.map((c) => c.uptimePct90d));
    const status = worstOfStatuses(components.map((c) => c.status));
    result.push({
      ...group,
      uptimePct90d,
      status,
      components,
    });
  }
  return result;
}
